import React from "react";
import { Box, Typography, Stack, Chip } from "@mui/material";
import { BsAward } from "react-icons/bs";
import { colors, font, pagePx } from "../theme/tokens";
import SectionHeading from "../components/SectionHeading";
import Reveal from "../components/Reveal";

// ---- Professional experience (with company logos) ----
const experience = [
  {
    role: "AI Software Engineering Intern",
    company: "LIRMM",
    logo: "/assets/logo_lirmm.png",
    meta: "Internship · Remote",
    period: "Jan 2026 – Jun 2026 · 6 mos",
    text: "Research-oriented AI software engineering internship at LIRMM (Montpellier Laboratory of Informatics, Robotics and Microelectronics).",
    current: true,
  },
  {
    role: "Back End Developer",
    company: "OrderTech",
    logo: "/assets/logo_ordertech.png",
    meta: "Full-time · Remote",
    period: "Aug 2025 – Jan 2026 · 6 mos",
    text: "Built backend services for an AI voice-ordering SaaS for restaurants — NestJS APIs, PostgreSQL, and POS integrations.",
  },
  {
    role: "Frontend Developer",
    company: "AET Industries",
    logo: "/assets/logo_aet.png",
    meta: "Part-time · Remote",
    period: "Nov 2024 – Jan 2025 · 3 mos",
    text: "Developed responsive web front-ends and UI components for client-facing products.",
  },
];

// ---- Education ----
const education = [
  {
    period: "2021 — 2026",
    title: "Software Engineering Degree (Engineer's Diploma)",
    org: "École Supérieure en Informatique (ESI-SBA), Algeria",
    text: "5-year engineering program. Specialization in Information Systems & Web. Coursework across distributed systems, microservices (Spring Boot), data engineering (Data Warehousing, ETL/OLAP, NoSQL), DevOps (Docker, Kubernetes), and full-stack development.",
    current: true,
  },
  {
    period: "2025 — 2026",
    title: "Capstone: AI-Native E-Commerce Platform (PFE)",
    org: "Final-Year Engineering Project",
    text: "Designed VirtuFit (an 8-service, event-driven virtual try-on platform) and Orchestrix (a WhatsApp-native LangGraph conversational-commerce platform), with emphasis on software architecture and quality attributes (ISO/IEC 25010).",
  },
  {
    period: "2021",
    title: "Baccalauréat — Mathematics (Highest Honors)",
    org: "Lycée Ahmed El-Bayrouni, Maghnia",
    text: "Graduated with an 'Excellent' distinction.",
    award: true,
  },
];

const Education = () => {
  return (
    <Box component="section" id="experience" sx={{ px: pagePx, py: { xs: 8, md: 12 } }}>
      <SectionHeading eyebrow="Experience & Education" title="Where I've worked and studied" />

      {/* Professional experience */}
      <Typography sx={{ fontFamily: font.mono, fontSize: 12.5, letterSpacing: 2, textTransform: "uppercase", color: colors.inkMute, mb: 3, maxWidth: 900, mx: "auto" }}>
        // Professional Experience
      </Typography>
      <Box sx={{ maxWidth: 900, mx: "auto", mb: { xs: 7, md: 9 } }}>
        <Stack spacing={2}>
          {experience.map((e, i) => (
            <Reveal key={e.company} delay={i * 80}>
              <Box
                sx={{
                  display: "flex", gap: { xs: 2, md: 3 }, alignItems: "flex-start",
                  p: { xs: 2.2, md: 3 }, borderRadius: "16px", backgroundColor: colors.surface,
                  border: `1px solid ${colors.border}`, transition: "all 0.2s ease",
                  "&:hover": { borderColor: "rgba(99,102,241,0.45)", transform: "translateY(-2px)" },
                }}
              >
                <Box
                  sx={{
                    flexShrink: 0, width: { xs: 52, md: 60 }, height: { xs: 52, md: 60 }, borderRadius: "12px",
                    backgroundColor: "#fff", display: "flex", alignItems: "center", justifyContent: "center",
                    p: 1, border: `1px solid ${colors.border}`,
                  }}
                >
                  <Box component="img" src={e.logo} alt={`${e.company} logo`} sx={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }} />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ sm: "center" }} sx={{ gap: 0.5, mb: 0.5 }}>
                    <Typography sx={{ fontFamily: font.sans, fontWeight: 600, fontSize: { xs: 16, md: 18 }, color: colors.ink }}>
                      {e.role}
                    </Typography>
                    <Chip label={e.period} size="small" sx={{ backgroundColor: "rgba(255,255,255,0.04)", color: e.current ? colors.accent : colors.inkSoft, border: `1px solid ${colors.border}`, fontFamily: font.mono, fontSize: 11 }} />
                  </Stack>
                  <Typography sx={{ fontSize: 14, color: colors.accent, fontWeight: 500, mb: 0.8 }}>
                    {e.company} <Box component="span" sx={{ color: colors.inkMute, fontWeight: 400 }}>· {e.meta}</Box>
                  </Typography>
                  <Typography sx={{ fontSize: 13.5, color: colors.inkSoft, lineHeight: 1.6 }}>{e.text}</Typography>
                </Box>
              </Box>
            </Reveal>
          ))}
        </Stack>
      </Box>

      {/* Education timeline */}
      <Typography sx={{ fontFamily: font.mono, fontSize: 12.5, letterSpacing: 2, textTransform: "uppercase", color: colors.inkMute, mb: 3, maxWidth: 820, mx: "auto" }}>
        // Education
      </Typography>
      <Box sx={{ maxWidth: 820, mx: "auto", position: "relative" }}>
        <Box sx={{ position: "absolute", left: 8, top: 8, bottom: 8, width: "2px", background: colors.border }} />
        {education.map((item, i) => (
          <Reveal key={i} delay={i * 80}>
            <Box sx={{ display: "flex", gap: 3, mb: 4, position: "relative" }}>
              <Box
                sx={{
                  flexShrink: 0, mt: 0.5, ml: "-1px", width: 18, height: 18, borderRadius: "50%",
                  background: item.current ? colors.gradPrimary : item.award ? colors.amber : colors.surface,
                  border: `2px solid ${item.current ? colors.accent : colors.borderStrong}`,
                  boxShadow: item.current ? "0 0 0 4px rgba(34,211,238,0.15)" : "none", zIndex: 1,
                }}
              />
              <Box sx={{ pb: 1 }}>
                <Chip label={item.period} size="small" sx={{ backgroundColor: "rgba(255,255,255,0.04)", color: colors.inkSoft, border: `1px solid ${colors.border}`, fontFamily: font.mono, fontSize: 11.5, mb: 1 }} />
                <Typography sx={{ fontFamily: font.sans, fontWeight: 600, fontSize: { xs: 16, md: 18 }, color: colors.ink, display: "flex", alignItems: "center", gap: 1 }}>
                  {item.title}
                  {item.award && <BsAward style={{ color: colors.amber }} />}
                </Typography>
                <Typography sx={{ fontSize: 14, color: colors.accent, fontWeight: 500, mb: 1 }}>{item.org}</Typography>
                <Typography sx={{ fontSize: 14, color: colors.inkSoft, lineHeight: 1.7 }}>{item.text}</Typography>
              </Box>
            </Box>
          </Reveal>
        ))}
      </Box>
    </Box>
  );
};

export default Education;
